import { create } from 'zustand';
import {
  Message,
  SignHistory,
  ConnectionStatus,
  RecognitionPhase,
  TranscriptWord,
} from '../types/libras';

interface SessionState {
  messages: Message[];
  signHistory: SignHistory[];
  currentSign: { label: string; confidence: number } | null;
  connectionStatus: ConnectionStatus;
  isRecording: boolean;
  handsDetected: boolean;
  recognitionPhase: RecognitionPhase;
  recognitionDetail: string | null;
  partialSign: { label: string; text: string; confidence: number; margin: number; timestamp: number } | null;
  transcriptWords: TranscriptWord[];

  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  addSignHistory: (sign: SignHistory) => void;
  setCurrentSign: (sign: { label: string; confidence: number } | null) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setIsRecording: (isRecording: boolean) => void;
  setRecognitionStatus: (handsDetected: boolean, phase: RecognitionPhase, detail?: string) => void;
  setPartialSign: (sign: { label: string; text: string; confidence: number; margin: number; timestamp: number } | null) => void;
  addTranscriptWord: (word: Omit<TranscriptWord, 'id'>) => void;
  removeLastTranscriptWord: () => void;
  clearTranscript: () => void;
  confirmTranscript: () => void;
  clearSession: () => void;
}

const DUPLICATE_WORD_WINDOW_MS = 2500;
const DUPLICATE_MESSAGE_WINDOW_MS = 6000;
// Signs recognized while the patient is still signing are auto-sent to the
// chat after this pause (ms) so the flow doesn't require a manual click.
export const AUTO_CONFIRM_DELAY_MS = 1500;
export const PARTIAL_AUTO_SEND_DELAY_MS = 1200;
const PARTIAL_AUTO_SEND_MIN_CONFIDENCE = 0.9;
const PARTIAL_AUTO_SEND_MIN_MARGIN = 0.2;
let autoConfirmTimer: ReturnType<typeof setTimeout> | null = null;
let partialAutoSendTimer: ReturnType<typeof setTimeout> | null = null;
let partialAutoSendKey: string | null = null;

const clearAutoConfirmTimer = () => {
  if (autoConfirmTimer) {
    clearTimeout(autoConfirmTimer);
    autoConfirmTimer = null;
  }
};

const clearPartialAutoSendTimer = () => {
  if (partialAutoSendTimer) {
    clearTimeout(partialAutoSendTimer);
    partialAutoSendTimer = null;
  }
  partialAutoSendKey = null;
};

const clearRecognitionTimers = () => {
  clearAutoConfirmTimer();
  clearPartialAutoSendTimer();
};

const scheduleAutoConfirm = (confirmTranscript: () => void) => {
  clearAutoConfirmTimer();
  autoConfirmTimer = setTimeout(() => {
    autoConfirmTimer = null;
    confirmTranscript();
  }, AUTO_CONFIRM_DELAY_MS);
};

const schedulePartialAutoSend = (
  sign: { label: string; text: string; confidence: number; margin: number; timestamp: number },
  getState: () => SessionState,
) => {
  if (sign.confidence < PARTIAL_AUTO_SEND_MIN_CONFIDENCE || sign.margin < PARTIAL_AUTO_SEND_MIN_MARGIN) {
    return;
  }

  const key = `${sign.label}\n${sign.text}`;
  if (partialAutoSendTimer && partialAutoSendKey === key) return;

  clearPartialAutoSendTimer();
  partialAutoSendKey = key;
  partialAutoSendTimer = setTimeout(() => {
    partialAutoSendTimer = null;
    partialAutoSendKey = null;
    const state = getState();
    const latest = state.partialSign;

    if (!latest || latest.label !== sign.label || latest.text !== sign.text) {
      return;
    }

    state.addTranscriptWord({
      label: latest.label,
      text: latest.text,
      confidence: latest.confidence,
      timestamp: latest.timestamp,
    });
    state.confirmTranscript();
  }, PARTIAL_AUTO_SEND_DELAY_MS);
};

export const useSessionStore = create<SessionState>((set, get) => ({
  messages: [],
  signHistory: [],
  currentSign: null,
  connectionStatus: 'disconnected',
  isRecording: false,
  handsDetected: false,
  recognitionPhase: 'idle',
  recognitionDetail: null,
  partialSign: null,
  transcriptWords: [],

  addMessage: (message) => set((state) => ({
    messages: [...state.messages, {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
    }]
  })),
  
  addSignHistory: (sign) => set((state) => ({
    signHistory: [...state.signHistory.slice(-7), sign]
  })),
  
  setCurrentSign: (sign) => set({ currentSign: sign }),
  
  setConnectionStatus: (status) => set({ connectionStatus: status }),
  
  setIsRecording: (isRecording) => {
    if (!isRecording) {
      const { partialSign, transcriptWords } = get();
      if (
        transcriptWords.length === 0 &&
        partialSign &&
        partialSign.confidence >= PARTIAL_AUTO_SEND_MIN_CONFIDENCE &&
        partialSign.margin >= PARTIAL_AUTO_SEND_MIN_MARGIN
      ) {
        get().addTranscriptWord({
          label: partialSign.label,
          text: partialSign.text,
          confidence: partialSign.confidence,
          timestamp: partialSign.timestamp,
        });
      }
      get().confirmTranscript();
    }

    set({
      isRecording,
      ...(!isRecording ? {
        handsDetected: false,
        recognitionPhase: 'idle' as RecognitionPhase,
        partialSign: null,
      } : {}),
    });
  },

  setRecognitionStatus: (handsDetected, recognitionPhase, detail = '') => set({
    handsDetected,
    recognitionPhase,
    recognitionDetail: detail || null,
  }),

  setPartialSign: (partialSign) => {
    if (!partialSign) {
      clearPartialAutoSendTimer();
      set({ partialSign: null });
      return;
    }

    set({ partialSign });
    schedulePartialAutoSend(partialSign, get);
  },

  addTranscriptWord: (word) => {
    let addedWord = false;

    set((state) => {
      const previous = state.transcriptWords[state.transcriptWords.length - 1];
      if (previous?.label === word.label && word.timestamp - previous.timestamp < DUPLICATE_WORD_WINDOW_MS) {
        return { partialSign: null };
      }
      addedWord = true;
      return {
        transcriptWords: [...state.transcriptWords, {
          ...word,
          id: `word-${word.timestamp}-${Math.random()}`,
        }],
        partialSign: null,
      };
    });

    if (addedWord) {
      scheduleAutoConfirm(get().confirmTranscript);
    }
  },

  removeLastTranscriptWord: () => {
    clearAutoConfirmTimer();
    set((state) => ({ transcriptWords: state.transcriptWords.slice(0, -1) }));

    if (get().transcriptWords.length > 0) {
      scheduleAutoConfirm(get().confirmTranscript);
    }
  },

  clearTranscript: () => {
    clearRecognitionTimers();
    set({ transcriptWords: [], partialSign: null });
  },

  confirmTranscript: () => {
    clearRecognitionTimers();
    set((state) => {
      const text = state.transcriptWords.map((word) => word.text).join(' ').trim();
      if (!text) return state;
      const lastPatientMessage = [...state.messages].reverse().find((message) => message.sender === 'patient');
      if (
        lastPatientMessage?.text === text &&
        Date.now() - lastPatientMessage.timestamp < DUPLICATE_MESSAGE_WINDOW_MS
      ) {
        return {
          transcriptWords: [],
          partialSign: null,
        };
      }
      return {
        messages: [...state.messages, {
          id: `msg-${Date.now()}-${Math.random()}`,
          sender: 'patient' as const,
          text,
          timestamp: Date.now(),
        }],
        transcriptWords: [],
        partialSign: null,
      };
    });
  },

  clearSession: () => {
    clearRecognitionTimers();
    set({
      messages: [],
      signHistory: [],
      currentSign: null,
      transcriptWords: [],
      partialSign: null,
      handsDetected: false,
      recognitionPhase: 'idle',
      recognitionDetail: null,
    });
  },
}));
